import React, { createContext, useEffect, useState, type ReactNode } from 'react';

import type { ColorTheme, ThemeMode } from '@/hooks/useTheme';
import { settingsService } from '@/stores/storeIntegration';
import { useSettings } from '@/stores/useAppStore';
import { logger } from '@/utils/logger';

const VALID_COLOR_THEMES: ColorTheme[] = [
  'lavender-mist',
  'soft-cloud',
  'paper-white',
  'classic',
  'winter',
  'halloween',
  'cherry-blossom',
  'fall',
  'patriotic',
  'st-patricks',
];
const DEFAULT_COLOR_THEME: ColorTheme = 'lavender-mist';

interface ThemeContextType {
  themeMode: ThemeMode;
  effectiveTheme: 'light' | 'dark';
  colorTheme: ColorTheme;
  setThemeMode: (mode: ThemeMode) => void;
  setColorTheme: (theme: ColorTheme) => void;
  toggleDarkMode: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { getSetting } = useSettings();

  // Track the OS-level preference as reactive state
  const [systemPreference, setSystemPreference] = useState<'light' | 'dark'>(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  );

  // Initialise themeMode
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const stored = getSetting('theme_mode') as ThemeMode;
    if (stored && ['light', 'dark', 'system'].includes(stored)) return stored;

    const local = localStorage.getItem('theme');
    if (local === 'light' || local === 'dark') return local;

    return 'system';
  });

  // Initialise colorTheme
  const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => {
    const stored = getSetting('color_theme') as ColorTheme;
    if (stored && VALID_COLOR_THEMES.includes(stored)) return stored;
    return DEFAULT_COLOR_THEME;
  });

  const effectiveTheme: 'light' | 'dark' = themeMode === 'system' ? systemPreference : themeMode;

  // Apply dark mode class to <html>
  useEffect(() => {
    const classList = document.documentElement.classList;
    if (effectiveTheme === 'dark') {
      classList.add('dark');
    } else {
      classList.remove('dark');
    }
  }, [effectiveTheme]);

  // Apply color theme via data-theme attribute on <html>. Single writer of
  // data-theme; seasonal themes are just regular selectable colorThemes now.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', colorTheme);
  }, [colorTheme]);

  // Listen for OS theme changes and update systemPreference
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Sync with settings whenever they change. Adjust during render (guarded so it only
  // fires when the stored value actually differs from current state) rather than in an
  // effect — React's recommended alternative to a synchronous setState inside useEffect.
  const storedMode = getSetting('theme_mode') as ThemeMode;
  if (storedMode && ['light', 'dark', 'system'].includes(storedMode) && storedMode !== themeMode) {
    setThemeModeState(storedMode);
  }
  const storedColor = getSetting('color_theme') as ColorTheme;
  if (storedColor && VALID_COLOR_THEMES.includes(storedColor) && storedColor !== colorTheme) {
    setColorThemeState(storedColor);
  }

  const setThemeMode = async (mode: ThemeMode): Promise<void> => {
    setThemeModeState(mode);
    try {
      await settingsService.updateSetting('theme_mode', mode, 'User preferred theme mode');
    } catch (error) {
      logger.error('Failed to save theme mode', error);
    }
    localStorage.removeItem('theme'); // clean legacy
  };

  const setColorTheme = async (theme: ColorTheme): Promise<void> => {
    setColorThemeState(theme);
    try {
      await settingsService.updateSetting('color_theme', theme, 'User preferred color theme');
    } catch (error) {
      logger.error('Failed to save color theme', error);
    }
  };

  const toggleDarkMode = (): void => {
    setThemeMode(effectiveTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        effectiveTheme,
        colorTheme,
        setThemeMode,
        setColorTheme,
        toggleDarkMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
